export type FilterOperator =
  | '_eq' | '_neq'
  | '_lt' | '_lte' | '_gt' | '_gte'
  | '_contains' | '_ncontains'
  | '_null' | '_nnull';

export type MetricType = 'count' | 'sum' | 'avg';
export type ReportViz = 'bar' | 'table' | 'multi-bar';
export type ReportMode = 'single' | 'multi';
// 'server' = Directus aggregation API (fast, no relational groupBy)
// 'client' = fetch raw rows, aggregate in browser (supports relational fields, enables leaderboards)
export type AggregationMode = 'server' | 'client';

// ── Date presets ───────────────────────────────────────────────────────────
export type DatePreset =
  | 'today' | 'yesterday'
  | 'this_week' | 'last_week'
  | 'this_month' | 'last_month' | 'last_30_days'
  | 'last_3_months' | 'last_6_months'
  | 'this_year' | 'last_year';

export interface DatePresetFilter {
  field: string;      // the datetime field to filter on, e.g. 'date_created'
  preset: DatePreset;
}

export const DATE_PRESET_OPTIONS: Array<{ value: DatePreset; label: string }> = [
  { value: 'today',         label: 'Today' },
  { value: 'yesterday',     label: 'Yesterday' },
  { value: 'this_week',     label: 'This week' },
  { value: 'last_week',     label: 'Last week' },
  { value: 'this_month',    label: 'This month' },
  { value: 'last_month',    label: 'Last month' },
  { value: 'last_30_days',  label: 'Last 30 days' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'last_6_months', label: 'Last 6 months' },
  { value: 'this_year',     label: 'This year' },
  { value: 'last_year',     label: 'Last year' },
];

// ── Core types ─────────────────────────────────────────────────────────────
export interface ReportFilter {
  field: string;
  operator: FilterOperator;
  value: string;
}

export interface ReportMetric {
  type: MetricType;
  field?: string;
}

export interface ReportSource {
  collection: string;
  label: string;
  filters: ReportFilter[];
  datePresets: DatePresetFilter[];   // resolved to _gte/_lte at query time
  metric: ReportMetric;
  displayColumns: string[];          // extra fields shown as table columns (client mode only)
}

export interface ReportDefinition {
  id: string;
  name: string;
  mode: ReportMode;
  aggregationMode: AggregationMode;
  sources: ReportSource[];
  groupBy: string | null;
  sortDesc: boolean;
  topN: number;
  viz: ReportViz;
  createdAt: string;
  updatedAt: string;
}

export interface ReportResultRow {
  label: string;
  values: number[];
  pct: number[];
  displayValues?: string[];   // extra column values for table view (client mode)
}

export interface ReportResult {
  rows: ReportResultRow[];
  seriesLabels: string[];
  metricLabels: string[];
  groupByLabel: string;
  rawData: Record<string, unknown>[][];
  displayColumnLabels: string[];    // headers for extra columns (empty in server mode)
}

export interface ResolvedField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'datetime';
  numeric: boolean;
  relatedCollection?: string;
}

export interface OperatorOption {
  value: FilterOperator;
  label: string;
  forTypes: ResolvedField['type'][];
}

export const OPERATOR_OPTIONS: OperatorOption[] = [
  { value: '_eq',        label: 'equals',               forTypes: ['string', 'number', 'boolean', 'datetime'] },
  { value: '_neq',       label: 'not equals',            forTypes: ['string', 'number', 'boolean', 'datetime'] },
  { value: '_lt',        label: 'less than',             forTypes: ['number', 'datetime'] },
  { value: '_lte',       label: 'less than or equal',    forTypes: ['number', 'datetime'] },
  { value: '_gt',        label: 'greater than',          forTypes: ['number', 'datetime'] },
  { value: '_gte',       label: 'greater than or equal', forTypes: ['number', 'datetime'] },
  { value: '_contains',  label: 'contains',              forTypes: ['string'] },
  { value: '_ncontains', label: "doesn't contain",       forTypes: ['string'] },
  { value: '_null',      label: 'is empty',              forTypes: ['string', 'number', 'boolean', 'datetime'] },
  { value: '_nnull',     label: 'is not empty',          forTypes: ['string', 'number', 'boolean', 'datetime'] },
];
